import {Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import FirestoreManager from '../../api/firebaseManager';
import {useRamp} from '../../hooks/use-ramp';
import '../../styles/plaid.css';

export default function RampPage({user}) {
  const navigate = useNavigate();
  const {connection_id} = useParams();
  const {
    state: {connections},
    removeConnection,
  } = useRamp();

  const connection = connections?.[connection_id] ?? {};
  const {entityName, entityNames, rampCompanyName} = connection;

  // FOR-2189: the entities the user saved. Older connections stored a single
  // entityName; newer ones store the checked list.
  const savedEntities =
    Array.isArray(entityNames) && entityNames.length ?
      entityNames :
      entityName ? [entityName] : [];

  const [syncing, setSyncing] = useState('');
  const [companyName, setCompanyName] = useState(rampCompanyName || '');

  // Backfill the Ramp company name for connections made before FOR-2189.
  useEffect(() => {
    let cancelled = false;
    if (rampCompanyName) {
      setCompanyName(rampCompanyName);
      return undefined;
    }
    if (!connection_id) return undefined;
    FirestoreManager.getRampCompanyName(connection_id)
      .then(res => {
        if (!cancelled && res?.success && res.rampCompanyName) {
          setCompanyName(res.rampCompanyName);
        }
      })
      .catch(() => {
        /* non-fatal: the screen just shows a dash */
      });
    return () => {
      cancelled = true;
    };
  }, [connection_id, rampCompanyName]);

  const runSync = async (label, fn) => {
    setSyncing(label);
    try {
      const result = await fn(connection_id);
      if (result?.success) {
        const c = result?.counts;
        const detail = c ?
          ` (${c.created} created, ${c.updated} updated, ${c.unchanged} unchanged)` :
          result?.count != null ? ` (${result.count} records)` : '';
        window.alert(`${label} sync complete${detail}.`);
      } else {
        window.alert(`${label} sync failed: ${result?.error || 'unknown error'}`);
      }
    } catch (err) {
      window.alert(`${label} sync failed: ${err?.message || 'unknown error'}`);
    }
    setSyncing('');
  };

  return (
    <div className='page-container'>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <div style={{flexGrow: 1}}>
          <Typography variant='h6' style={{marginBottom: '12px'}}>
            Ramp Connection
          </Typography>
          <Typography>
            Use the sync buttons to push the current Forest entities, chart of
            accounts, and payees to Ramp. Existing records in Ramp are matched
            and updated; new ones are created.
          </Typography>
          <br />
          <Typography>
            <strong>Ramp Company:</strong> {companyName || '—'}
          </Typography>
          <br />
          <table style={{maxWidth: '640px'}}>
            <thead>
              <tr>
                <th style={{textAlign: 'left'}}>Forest Entity</th>
              </tr>
            </thead>
            <tbody>
              {savedEntities.map(name => (
                <tr key={name}>
                  <td>{name}</td>
                </tr>
              ))}
              {!savedEntities.length && (
                <tr>
                  <td>No Forest entities saved for this connection.</td>
                </tr>
              )}
            </tbody>
          </table>
          {syncing && (
            <Typography sx={{fontStyle: 'italic', marginTop: '8px'}}>
              Syncing {syncing}...
            </Typography>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            marginLeft: '36px',
          }}
        >
          <button
            className='disconnect'
            disabled={!!syncing}
            onClick={() =>
              runSync('Entity', FirestoreManager.uploadRampEntities)
            }
            style={{marginBottom: '16px'}}
          >
            Sync Entity
          </button>
          <button
            className='disconnect'
            disabled={!!syncing}
            onClick={() =>
              runSync('Accounts', FirestoreManager.uploadRampAccounts)
            }
            style={{marginBottom: '16px'}}
          >
            Sync Accounts
          </button>
          <button
            className='disconnect'
            disabled={!!syncing}
            onClick={() =>
              runSync('Payees', FirestoreManager.uploadRampVendors)
            }
            style={{marginBottom: '16px'}}
          >
            Sync Payees
          </button>
          <button
            className='disconnect'
            disabled={!!syncing}
            onClick={async () => {
              const confirmed = window.confirm(
                'Disconnect this Ramp connection? This unlinks Forest as the ' +
                  'accounting provider inside Ramp and removes the connection ' +
                  'from Connect. It can be reconnected later.',
              );
              if (!confirmed) return;
              const {success, warning} = await removeConnection(connection_id);
              if (success) {
                if (warning) {
                  window.alert(
                    `Removed from Connect, but the Ramp-side unlink had an ` +
                      `issue: ${warning}. You may need to disconnect it from ` +
                      `within Ramp.`,
                  );
                }
                navigate('/dashboard');
              } else {
                window.alert('Failed to remove the connection.');
              }
            }}
            style={{marginBottom: '16px'}}
          >
            Disconnect
          </button>
          <button
            className='disconnect'
            onClick={() => navigate('/dashboard')}
            style={{marginBottom: '16px'}}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
